/**
 * @ngdoc service
 * @name EcourseFactory
 * @constructor
 */
export default function EcourseFactory(Ecourses) {
  'ngInject';
  
  /**
   * @name Find Segment
   * @param ecourse
   * @param identifier
   * @param key what field to compare the identifier with
   * @returns {T}
   */
  var segments;

  function findSegment(ecourse, identifier, key = 'slug') {
    return ecourse.segments.find((segment) => {
      if (segment[key] === identifier) { // set currentSegment
        if (this) {
          this.currentSegment = segment;
        }
      }
      return segment[key] === identifier;
    });
  }

  /**
   * @name Find Media
   * @param ecourse
   * @param mediaId
   * @returns {T}
   */
  function findMedia(ecourse, mediaId) {
    let media;
    return ecourse.segments.find(function (segment) {
      media = findMediaInSegment(segment, mediaId);
      this.currentMedia = media; //  current media
      return media;
    });
  }

  /**
   * @name Find Media In Segment
   * @param segment
   * @param mediaId
   * @returns {T}
   */
  function findMediaInSegment(segment, mediaId) {
    if (!segment.mediaContent || segment.mediaContent.length < 1) {
      return undefined;
    }
    let media = segment.mediaContent.find(function (media) {
      return media.id === mediaId;
    });
    return media;
  }

  /**
   * @name Find Progress
   * @description Return a object of the segment and media in the progress object
   * @param ecourse
   * @param mediaId
   * @returns {*}
   */
  function findProgress(ecourse, mediaId) {
    if (!ecourse.userData) return undefined;
    let media,
      segment = ecourse.userData.progress.find(function (segment) {
        media = findMediaInSegment(segment, mediaId);
        return media;
      });
    return {segment, media};
  }

  /**
   * @name Next
   * @description Based on the progress data get the next segment and video
   * @param ecourse The ecourse data
   */
  function next(ecourse) {
    if (!ecourse.userData) return undefined;
    //setup vars
    let segmentProgress, segment, mediaProgress, media;
    //Find segment in progress obj
    segmentProgress = ecourse.userData.progress.find(segment => !segment.complete) || {}; //first incomplete segment
    //Find the segment in the ecourse data
    segment = findSegment(ecourse, segmentProgress.id, 'id');
    //If segment found
    if (segment) {
      //Find the media in the progress obj
      mediaProgress = segmentProgress.mediaContent.find(function (media) {
        //First incomplete media
        return media.complete === false;
      });
      //If there is incomplete media in this segment
      if (mediaProgress) {
        //Find the media in the ecourse data
        media = findMediaInSegment(segment, mediaProgress.id);
      }
    }
    return {segment, media};
  }
    
  /**
   * @description loads the next segment
   * @param ecourse
   * @param segment
   * @param media
   */
  function nextInOrder(ecourse, segment, media) {
    if (!segment && segment.mediaContent.length === 0) {
      return {segment: segment, media: undefined}
    }
    let mediaIndex = segment.mediaContent.findIndex(function (m) {
      return m.id === media.id;
    });
    let newMedia = segment.mediaContent[mediaIndex + 1];
    if (newMedia) {
      return {segment, media: newMedia};
    }
    else {
      let segmentIndex = ecourse.segments.findIndex(function (s) {
        return s.slug === segment.slug;
      });
      let newSegment = ecourse.segments[segmentIndex + 1];
      if (newSegment) {
        return {segment: newSegment, media: newSegment.mediaContent[0]};
      }
      else {
        return {segment: undefined, media: undefined};
      }
    }
  }

  /**
   * @name  Segment Available
   * @description If the segment is published in the past.
   * @param segment
   * @returns {boolean}
   */
  function segmentAvailable(segment) {
    if (!segment.streamingStartDate) return true;
    let publish = new Date(segment.streamingStartDate),
      now = new Date();
    return publish.getTime() <= now.getTime();
  }
  
  /**
   * @description If the live stream is ove then the media is available.
   * @param segment
   * @returns {boolean}
   */
  function mediaAvailable(segment) {
    let publish = new Date(segment.streamingEndDate),
      now = new Date();
    return publish.getTime() <= now.getTime();
  }

  /**
   * @name SegmentProgress
   * @description Return an object representing the progress state of the segment progress object. Returns {percentage, complete and incomplete}.
   * @param progressData
   * @returns {*}
   */
  function segmentProgress(progressData) {
    //Setup vars
    let percentage, completeNumber = 0, incompleteNumber = 0, noOfMedia;
    //Total segment length
    noOfMedia = progressData.mediaContent.length;
    //Return a complete progress if the segment is complete
    if (progressData.complete === true) {
      return {
        percentage: 100,
        completeNumber: noOfMedia,
        incompleteNumber: incompleteNumber
      };
    }
    //Count complete mediaContent within segment
    progressData.mediaContent.map(function (media) {
      if (media.complete === true) {
        completeNumber += 1;
      }
    });
    //Calculate percentage of completion.
    percentage = Math.abs(Math.floor((completeNumber / noOfMedia ) * 100));
    //Remaining videos.
    incompleteNumber = noOfMedia - completeNumber;
    return {
      percentage,
      completeNumber,
      incompleteNumber
    };
  }

  /**
   * @name progressPristine
   * @description Returns the status of the progress; ie if the user has watched anything yet.
   * @return Boolean
   */
  function progressPristine(progress) {
    let progressMade = progress.find(function (segment) {
      if (segment.complete) return true;
      else return segment.mediaContent.find(function (mediaProgress) {
        return mediaProgress.complete || mediaProgress.position > 0;
      });
    });
    return progressMade ? false : true;
  }

  /**
   * @name nextLiveSegment
   * @description Returns the segment that has a streamingStartDate that is closest to now.
   * @param ecourse
   */
  function nextLiveSegment(ecourse) {

    function future(d) {
      return d.getTime() > Date.now();
    }

    function segDate(seg) {
      return new Date(seg.streamingStartDate);
    }

    //Get only future segs.
    let futureSegs = ecourse.segments.filter(function (seg) {
      return future(segDate(seg));
    });
    if (futureSegs && futureSegs.length > 0) {
      //Reduce them to the closest one.
      let seg = futureSegs.reduce(function (prev, curr) {
        let currDate = segDate(curr),
          prevDate = segDate(prev),
          closerThanPrev = currDate.getTime() < prevDate.getTime(),
          res = closerThanPrev ? curr : prev;
        return res;
      });
      return seg;
    }
    else {
      return undefined;
    }
  }

  function findLiveSegment(ecourse) {
    return ecourse.segments.find(function (segment) {
      return isLive(segment);
    });
  }

  function isLive(segment) {
    let now = Date.now(),
      st = new Date(segment.streamingStartDate),
      end = new Date(segment.streamingEndDate);
    return now > st.getTime() && end.getTime() > now;
  }

  function ecourseComplete(progress) {
    const actualProgress = progress.filter(item => !!item.id);
    const len = actualProgress.length;
    const completeLen = actualProgress.reduce((value, current) => current.complete ? value + 1 : value, 0);

    return completeLen === len;
  }

  //MUTATORS
  function complete(segment, media) {
    media.complete = true;
    let incomplete = segment.mediaContent.find(function (media) {
      return media.complete === false;
    });
    if (incomplete === undefined) {
      segment.complete = true;
    }
  }


  /*
   return currentSegment for player.directive
  */
  function getSegments() {
    return this.currentSegment;
  }
  
  return {
    findSegment,
    findMedia,
    findMediaInSegment,
    findProgress,
    next,
    nextInOrder,
    segmentProgress,
    segmentAvailable,
    mediaAvailable,
    progressPristine,
    ecourseComplete,
    nextLiveSegment,
    findLiveSegment,
    isLive,
    complete,
    getSegments
  };
}



/** WEBPACK FOOTER **
 ** ./src/app/services/ecourse/ecourse.factory.js
 **/